import historicalRpiData from "./historicalRpiData.json";

/**
 * Historical and current UK student loan rates/thresholds, keyed by year.
 *
 * Key semantics (read this before making the annual update):
 *  - Threshold objects (currentPaymentFloor, currentRateCeiling, plan1Floor,
 *    plan4Floor, plan5Floor): key N means "the figure for tax year N/(N+1)"
 *    (tax year N/(N+1) runs 6 Apr N - 5 Apr N+1).
 *  - Interest snapshot objects (rpi, plan1Rate, interestCap): key N
 *    means "the figure for the academic/interest window Sept N - Aug N+1".
 *  - Interest entries are snapshots at 1 September, not a full record of
 *    within-year base-rate/cap changes. They are retained for the annual
 *    reference tables; historicalLookup.js supplies dated rates to the engine.
 *  - monthlyRpi and monthlyRpiIndex contain real ONS calendar-month data.
 *    Array index 0 is January. Partial years stop at the last published month.
 *    Monthly inflation is separate from the March-based September loan rate.
 *  - Lookups elsewhere in the app (see calculator.js) are exact-key lookups
 *    against `data[i].year` - there is no "nearest known year" fallback, so
 *    a gap for an intermediate year simply means that year doesn't pick up
 *    a historical override (it is not itself a bug to leave a year out).
 *  - defaultPlanFloor holds the *current* tax year's threshold per plan and
 *    must always match the current year's entry in the threshold objects
 *    above (and should match ratesData.js - see ratesFreshness.test.js).
 *  - interestCap caps the calculated interest rate for a plan/year; a plan
 *    mapped to `null` (e.g. plan1NI) has no separate cap entry because it
 *    follows plan1's rate/cap directly in calculator.js.
 *
 * Annual update (every April for thresholds, every September for interest):
 *  1. Add a new key to each relevant object above for the new year.
 *  2. Update defaultPlanFloor and interestCap to the new current-year figures.
 *  3. Update ratesData.js to match (see that file's header comment).
 *  Sources checked 11 October 2026:
 *  https://www.gov.uk/guidance/previous-annual-repayment-thresholds
 *  https://www.gov.uk/guidance/how-interest-is-calculated-plan-1
 *  https://www.gov.uk/guidance/how-interest-is-calculated-plan-2
 *  https://www.gov.uk/guidance/how-interest-is-calculated-postgraduate-loan
 *  https://www.gov.uk/government/news/student-loans-interest-rates-and-repayment-threshold-announcement--3
 *  https://www.gov.uk/government/news/student-loans-interest-rates-and-repayment-threshold-announcement--4
 *  https://www.gov.uk/government/news/student-loans-interest-rates-and-repayment-threshold-announcement--5
 *  https://www.gov.uk/government/news/student-loans-interest-rates-and-repayment-threshold-announcement--6
 */
let historicalData = {
  rpi: {
    2026: 4.1,
    2025: 3.2,
    2024: 4.3,
    2023: 13.5,
    2022: 9.0,
    2021: 1.5,
    2020: 2.6,
    2019: 2.4,
    2018: 3.3,
    2017: 3.1,
    2016: 1.6,
    2015: 0.9,
    2014: 2.5,
    2013: 3.3,
    2012: 3.6,
  },
  currentPaymentFloor: {
    2026: 29385,
    2025: 28470,
    2024: 27295,
    2023: 27295,
    2022: 27295,
    2021: 27295,
    2020: 26575,
    2019: 25725,
    2018: 25000,
    2017: 21000,
    2016: 21000,
    2015: 21000,
    2014: 21000,
    2013: 21000,
    2012: 21000,
  },
  currentRateCeiling: {
    2026: 52885,
    2025: 51245,
    2024: 49130,
    2023: 49130,
    2022: 49130,
    2021: 49130,
    2020: 47835,
    2019: 46305,
    2018: 45000,
    2017: 41000,
    2016: 41000,
    2015: 41000,
    2014: 41000,
    2013: 41000,
    2012: 41000,
  },
  plan1Floor: {
    2000: 10000,
    2001: 10000,
    2002: 10000,
    2003: 10000,
    2004: 10000,
    2005: 15000,
    2006: 15000,
    2007: 15000,
    2008: 15000,
    2009: 15000,
    2010: 15000,
    2011: 15000,
    2012: 15795,
    2013: 16365,
    2014: 16910,
    2015: 17335,
    2016: 17495,
    2017: 17775,
    2018: 18330,
    2019: 18935,
    2020: 19390,
    2021: 19895,
    2022: 20195,
    2023: 22015,
    2024: 24990,
    2026: 26900,
    2025: 26065,
    2027: 28005,
  },
  plan4Floor: {
    2000: 10000,
    2001: 10000,
    2002: 10000,
    2003: 10000,
    2004: 10000,
    2005: 15000,
    2006: 15000,
    2007: 15000,
    2008: 15000,
    2009: 15000,
    2010: 15000,
    2011: 15000,
    2012: 15795,
    2013: 16365,
    2014: 16910,
    2015: 17335,
    2016: 17495,
    2017: 17775,
    2018: 18330,
    2019: 18935,
    2020: 19390,
    2021: 25000,
    2022: 25375,
    2023: 27660,
    2024: 31395,
    2026: 33795,
    2025: 32745,
  },
  plan5Floor: {
    2026: 25000,
    2023: 25000,
    2024: 25000,
    2025: 25000,
  },
  plan1Rate: {
    2026: 4.1,
    2025: 3.2,
    2024: 4.3,
    2023: 6.25,
    2022: 2.75,
    2021: 1.1,
    2020: 1.1,
    2019: 1.75,
    2018: 1.75,
    2017: 1.25,
    2016: 1.25,
    2015: 0.9,
    2014: 1.5,
    2013: 1.5,
    2012: 1.5,
    2011: 1.5,
    2010: 1.5,
    2009: 0.0,
    2008: 3.8,
    2007: 4.8,
    2006: 2.4,
    2005: 3.2,
    2004: 2.6,
    2003: 3.1,
    2002: 1.3,
    2001: 2.3,
    2000: 2.6,
    1999: 2.1,
    1998: 3.5,
  },
  defaultPlanFloor: {
    plan1: 26900,
    plan1NI: 26900,
    plan2: 29385,
    plan4: 33795,
    plan5: 25000,
    planPostgrad: 21000,
  },
  interestCap: {
    plan1: { 2021: 1.1, 2022: 2.75, 2023: 6.25, 2024: 4.3, 2025: 3.2, 2026: 4.1 },
    // plan1NI has no separate cap entry - it follows plan1's rate/cap in calculator.js
    plan1NI: null,
    plan2: {
      2021: 4.2,
      2022: 6.3,
      2023: 7.3,
      2024: 7.3,
      2025: 6.2,
      2026: 6.0,
    },
    plan4: { 2021: 1.1, 2022: 2.75, 2023: 6.25, 2024: 4.3, 2025: 3.2, 2026: 4.1 },
    plan5: { 2023: 7.3, 2024: 4.3, 2025: 3.2, 2026: 4.1 },
    planPostgrad: {
      2021: 4.2,
      2022: 6.3,
      2023: 7.3,
      2024: 7.3,
      2025: 6.2,
      2026: 6.0,
    },
  },
  // ONS CZBH is twelve-month inflation; CHAW is the price index. The engine
  // uses published SLC dated rules for loan interest, not these monthly rates.
  monthlyRpi: historicalRpiData.monthlyRpi,
  monthlyRpiIndex: historicalRpiData.monthlyRpiIndex,
};

export default historicalData;
